import { APP_INITIALIZER, ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HttpBackend, HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { provideNativeDateAdapter } from '@angular/material/core';
import { IPublicClientApplication } from '@azure/msal-browser';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalService
} from '@azure/msal-angular';

import { routes } from './app.routes';
import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory
} from './core/auth/msal.config';
import { apiTokenInterceptor } from './core/auth/api-token.interceptor';
import { ApimKeyBackend, apimSubscriptionKeyInterceptor, loadApimConfig } from './core/apim/apim-subscription';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(
      withFetch(),
      withInterceptorsFromDi(),
      withInterceptors([apimSubscriptionKeyInterceptor, apiTokenInterceptor])
    ),

    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },

    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },

    // Bare HttpClient that bypasses every interceptor (no Bearer / tenant headers).
    // Used by the public form lookup, which hits an unauthenticated Corporate API endpoint.
    // It still carries the APIM subscription key, which the gateway needs on every call.
    {
      provide: 'NoInterceptorHttpClient',
      useFactory: (handler: HttpBackend) => new HttpClient(new ApimKeyBackend(handler)),
      deps: [HttpBackend]
    },

    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [MSAL_INSTANCE],
      useFactory: (msal: IPublicClientApplication) => () => msal.initialize()
    },

    // The API Management keys, fetched before the first API call. Runs alongside MSAL's start-up; it gives
    // up after a few seconds rather than hold the app on a blank page.
    { provide: APP_INITIALIZER, multi: true, useValue: () => loadApimConfig() },

    MsalService,
    MsalGuard,
    MsalBroadcastService,

    // Every datepicker needs a DateAdapter. Provided once here rather than per component:
    // MatDialog resolves against the injector of whoever opened the dialog, so a dialog that
    // imports MatNativeDateModule itself still threw "No provider for DateAdapter!".
    provideNativeDateAdapter(),

    // fitInkBarToContent: the underline sits under the label instead of spanning the tab's whole
    // cell. Stretched tabs make that cell half the header, and a line running to the midpoint
    // read as though both tabs were selected.
    { provide: MAT_TABS_CONFIG, useValue: { fitInkBarToContent: true } },

    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { hasBackdrop: true, panelClass: 'klidi-dialog' } },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 3000, horizontalPosition: 'right', verticalPosition: 'bottom' } }
  ]
};
